import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import alertify from 'alertifyjs';
import { Subscription } from 'rxjs';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { VendorRow, VendorsResponse } from '../../vendors/vendor-landing/vendor-landing.model';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { GenericListResponse, GenericResponse } from '../../../modules/shared/models/general.modal';
import { Router, RouterLink } from '@angular/router';
import { FilterDrawerFieldConfig, FilterDrawerValue } from '../../../components/filter-drawer/filter-drawer.models';
import { FilterDrawerComponent } from "../../../components/filter-drawer/filter-drawer.component";
import { CategoryOption, MdaListResponse, MdaOption } from '../../../modules/services/utility-service.models';
import { DropdownMenu } from "../../../components/dropdown-menu/dropdown-menu.component";
import { AuthenticationService } from '../../../modules/services/authentication.service';
import { WorkflowNotificationService } from '../../../modules/services/workflow-notification.service';

type VendorDetailsTab = 'overview' | 'directors' | 'bank' | 'documents' | 'comments';

type VendorApprovalStatusFilter = 'ALL' | string;
type VendorPaymentStatusFilter = 'ALL' | string;

type VendorFilter = {
  vendorId: string;
  vendorName: string;
  asin: string;
  mdaId: string;
  categoryId: string;
  approvalStatus: VendorApprovalStatusFilter;
  paymentStatus: VendorPaymentStatusFilter;
};


@Component({
  selector: 'app-user-vendors',
  standalone: true,
  imports: [CommonModule, RouterLink, FilterDrawerComponent, DropdownMenu],
  templateUrl: './user-vendors.component.html',
  styleUrl: './user-vendors.component.scss',
})
export class UserVendorsComponent implements OnInit, OnDestroy {
  vendors: VendorRow[] = [];

  selectedVendor: VendorRow | null = null;
  vendorDetails: VendorRow | null = null;
  isLoadingVendorDetails = false;
  isLoadingVendors: boolean = false;

  readonly pageSize = 10;
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  tab: VendorDetailsTab = 'overview';
  openMenuIndex: number | null = null;

  readonly emptyFilters: VendorFilter = {
    vendorId: '',
    vendorName: '',
    asin: '',
    mdaId: '',
    categoryId: '',
    approvalStatus: 'ALL',
    paymentStatus: 'ALL'
  };

  vendorFilters: VendorFilter = { ...this.emptyFilters };
  vendorFilterFields: FilterDrawerFieldConfig[] = [];
  isFilterDrawerOpen = false;

  categoryOptions: CategoryOption[]
  mdaOptions: MdaOption[];

  private readonly sub = new Subscription();

  authenticationService: AuthenticationService = inject(AuthenticationService);

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly router: Router,
    private readonly workflowNotificationService: WorkflowNotificationService,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);
  }

  ngOnInit(): void {
    this.fetchMyVendors();
    this.getMdas();
    this.getCategories();
    this.workflowNotificationService.connect();
    this.sub.add(
      this.workflowNotificationService.workflowNotifications$.subscribe((notification) => {
        if (!notification.type?.startsWith('workflow.')) return;
        this.fetchMyVendors(this.currentPage);
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get activeFilterCount(): number {
    return Object.entries(this.vendorFilters).filter(([key, value]) => {
      if (key === 'approvalStatus' || key === 'paymentStatus') return value !== 'ALL';
      return String(value ?? '').trim().length > 0;
    }).length;
  }

  fetchMyVendors(page: number = 1): void {
    this.isLoadingVendors = true;
    this.openMenuIndex = null;
    const offset = (page - 1) * this.pageSize;

    let params = new HttpParams().set('offset', offset).set('limit', this.pageSize);
    const vendorId = this.vendorFilters.vendorId.trim();
    const vendorName = this.vendorFilters.vendorName.trim();
    const asin = this.vendorFilters.asin.trim();
    const mdaId = this.vendorFilters.mdaId.trim();
    const categoryId = this.vendorFilters.categoryId.trim();
    const approvalStatus = String(this.vendorFilters.approvalStatus ?? '').trim();
    const paymentStatus = String(this.vendorFilters.paymentStatus ?? '').trim();

    if (vendorId) params = params.set('vendor_reference', vendorId);
    if (vendorName) params = params.set('name', vendorName);
    if (asin) params = params.set('asin', asin);
    if (mdaId) params = params.set('mda_id', mdaId);
    if (categoryId) params = params.set('category_id', categoryId);
    if (approvalStatus && approvalStatus !== 'ALL') params = params.set('approval_status', approvalStatus);
    if (paymentStatus && paymentStatus !== 'ALL') params = params.set('payment_status', paymentStatus);

    this.apiService.get('/vendors/mine', { params }).subscribe({
      next: (response: VendorsResponse | any) => {
        const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        this.vendors = rows.map((item: any) => this.mapVendorRow(item));
        this.buildVendorFilterFields();

        this.selectedVendor = this.vendors[0] ?? null;
        this.fetchVendorDetails();
        this.totalItems = response.pagination?.totalItems ?? this.vendors.length;
        this.totalPages = response.pagination?.totalPages ?? 1;
        this.currentPage = response.pagination?.currentPage ?? page;
        this.hasNextPage = response.pagination?.hasNextPage ?? false;
        this.hasPreviousPage = response.pagination?.hasPreviousPage ?? false;
        this.isLoadingVendors = false;
      },
      error: (err) => {
        this.isLoadingVendors = false;
        this.vendors = [];
        this.selectedVendor = null;
        alertify.error(typeof err === 'string' ? err : 'Unable to fetch your vendor applications.');
      },
    });
  }

  getCategories(): void {
    this.apiService.get('/categories').subscribe({
      next: (response: GenericListResponse<CategoryOption>) => {
        if ("content" in response.data) {
          this.categoryOptions = response.data.content ?? []
          this.buildVendorFilterFields();
          return;
        };
        this.categoryOptions = response.data ?? [];
        this.buildVendorFilterFields();
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(error);
        }
      },
    });
  }

  getMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];
        this.buildVendorFilterFields();
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message);
        } else {
          alertify.error(error);
        }
      },
    });
  }

  getApplicationStatusColor(applicationStatus: string): string {
    switch(applicationStatus) {
      case "APPROVED":
        return "!text-[#008000]";
      case "PENDING":
        return "!text-[#C38513]";
      case "QUERIED":
        return "!text-[#D91818]";
      default:
        return ""
    }
  }

  getApplicationStatusPillColor(applicationStatus: string): string {
    switch(applicationStatus) {
      case "APPROVED":
        return "!bg-[#008000] !text-white";
      case "PENDING":
        return "!bg-[#C38513] !text-white";
      case "QUERIED":
        return "!bg-[#D91818] !text-white";
      default:
        return ""
    }
  }

  getPaymentStatusColor(paymentStatus: string): string {
    switch(paymentStatus) {
      case "PAID":
        return "!bg-[#E6FFE9] !text-[#289D35]";
      case "PENDING":
        return "!bg-[#FFF6D4] !text-[#A88205]";
      default:
        return ""
    }
  }

  selectVendor(vendor: VendorRow): void {
    this.selectedVendor = vendor;
    this.fetchVendorDetails();
  }

  fetchVendorDetails(): void {
    const vendor = this.selectedVendor;
    if (!vendor) return;

    this.isLoadingVendorDetails = true;
    this.vendorDetails = null;
    this.apiService.getVendorById(vendor.id).subscribe({
      next: (response: any) => {
        const item = response?.data ?? response;
        this.vendorDetails = this.mapVendorDetailsRow(item);
        this.isLoadingVendorDetails = false;
      },
      error: () => {
        this.isLoadingVendorDetails = false;
        alertify.error('Unable to fetch vendor details.');
      },
    });
  }

  goToPreviousPage(): void {
    if (!this.hasPreviousPage || this.isLoadingVendors) {
      return;
    }

    this.fetchMyVendors(this.currentPage - 1);
  }

  goToNextPage(): void {
    if (!this.hasNextPage || this.isLoadingVendors) {
      return;
    }

    this.fetchMyVendors(this.currentPage + 1);
  }

  toggleMenu(event: Event, index: number): void {
    event.stopPropagation();
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  handlePaymentReconciliation(applicationId: string) {
    const params = new HttpParams().set('applicationId', applicationId);
    this.apiService.post('/payment/reconcile', null, {params}).subscribe(
      {
        next: (response: GenericResponse<{orderId: string}>) => {
          if (response.status !== "success") {
            alertify.error("Failed to process payment reconciliation");
            return;
          }
          this.router.navigate(['user', 'payment', 'return-back', response.data.orderId]);
        }, 
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message || "Failed to initiate payment reconciliation");
          } else {
            alertify.error(JSON.stringify(error) || "Failed to initiate payment reconciliation");
          }
        }
      }
    )
  }

  handleReceiptDownload(vendorId: string): void {
    if (!String(vendorId ?? '').trim()) {
      alertify.error('Unable to start payment receipt download.');
      return;
    }

    this.openDownloadUrl(this.apiService.getReceiptDownloadUrl(vendorId), 'payment receipt');
  }

  handleDemandNoticeDownload(vendorId: string): void {
    if (!String(vendorId ?? '').trim()) {
      alertify.error('Unable to start demand notice download.');
      return;
    }

    this.openDownloadUrl(this.apiService.getDemandNoticeDownloadUrl(vendorId), 'demand notice');
  }

  handleCertificateDownload(vendorId: string): void {
    if (!String(vendorId ?? '').trim()) {
      alertify.error('Unable to start certificate download.');
      return;
    }

    this.openDownloadUrl(this.apiService.getCertificateDownloadUrl(vendorId), 'certificate');
  }

  applyFilters(raw: FilterDrawerValue): void {
    this.vendorFilters = {
      ...this.emptyFilters,
      ...Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [key, `${value ?? ''}`]),
      ),
    } as VendorFilter;

    const rawApprovalStatus = (raw['approvalStatus'] ?? 'ALL') as string;
    const rawPaymentStatus = (raw['paymentStatus'] ?? 'ALL') as string;
    this.vendorFilters.approvalStatus = rawApprovalStatus || 'ALL';
    this.vendorFilters.paymentStatus = rawPaymentStatus || 'ALL';

    this.isFilterDrawerOpen = false;
    this.fetchMyVendors(1);
  }

  closeFilters(): void {
    this.isFilterDrawerOpen = false;
  }

  openFilters(): void {
    this.openMenuIndex = null;
    this.isFilterDrawerOpen = true;
  }

  private openDownloadUrl(url: string, label: string): void {
    if (!url) {
      alertify.error(`Unable to start ${label} download.`);
      return;
    }

    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (!popup) {
      alertify.message(`${label} opened in this tab because the browser blocked a new tab.`);
      return;
    }
  }

  private buildVendorFilterFields(): void {
    const mdaOptions = Array.from(
      new Map(
        this.mdaOptions?.map((mda) => [
          mda.id,
          { label: mda.name || mda.id, value: mda.id },
        ]),
      ).values(),
    );

    const categoryOptions = Array.from(
      new Map(
        this.categoryOptions?.map((category) => [
          category.id,
          { label: category.name || category.id, value: category.id },
        ]),
      ).values(),
    );

    const approvalOptions = Array.from(
      ["APPROVED", "PENDING", "QUERIED"],
    ).sort((a, b) => a.localeCompare(b));

    const paymentStatusOptions = Array.from(
      ["PAID", "PENDING"]
    ).sort((a, b) => a.localeCompare(b));

    this.vendorFilterFields = [
      {
        key: 'vendorId',
        label: 'Vendor ID',
        type: 'text',
        placeholder: 'Filter by vendor ID',
      },
      {
        key: 'vendorName',
        label: 'Vendor Name',
        type: 'text',
        placeholder: 'Filter by vendor name',
      },
      {
        key: 'mdaId',
        label: 'MDA',
        type: 'searchable-select',
        placeholder: 'Select MDA',
        searchablePlaceholder: 'Search MDA options',
        options: mdaOptions,
      },
      {
        key: 'categoryId',
        label: 'Category',
        type: 'searchable-select',
        placeholder: 'Select category',
        searchablePlaceholder: 'Search category options',
        options: categoryOptions,
      },
      {
        key: 'approvalStatus',
        label: 'Approval Status',
        type: 'select',
        placeholder: 'Select approval status',
        options: ['ALL', ...approvalOptions].map((status) => ({
          label: status === 'ALL' ? 'All Approval Status' : status,
          value: status,
        })),
      },
      {
        key: 'paymentStatus',
        label: 'Payment Status',
        type: 'select',
        placeholder: 'Select payment status',
        options: ['ALL', ...paymentStatusOptions].map((status) => ({
          label: status === 'ALL' ? 'All Payment Status' : status,
          value: status,
        })),
      },
    ];
  }

  private mapVendorRow(item: any): VendorRow {
    const asin = String(item?.asin ?? '');
    const tinRaw = String(item?.tin ?? item?.tin_number ?? '').trim();

    return {
      id: String(item?.id ?? ''),
      name: String(item?.name ?? 'N/A'),
      email: String(item?.email ?? ''),
      vendorReference: String(item?.vendor_reference ?? item?.id ?? ''),
      mdaId: String(item?.mda_id ?? ''),
      mdaName: String(item?.mda_name ?? item?.mda_id ?? ''),
      categoryId: String(item?.category_id ?? ''),
      categoryName: String(item?.category_name ?? item?.category_id ?? ''),
      status: String(item?.status ?? 'PENDING'),
      approvalStatus: String(item?.approval_status ?? ''),
      paymentStatus: String(item?.payment_status ?? ''),
      paymentReference: String(item?.payment_reference ?? ''),
      asin,
      phoneNo: String(item?.phone_no ?? ''),
      address: String(item?.address ?? ''),
      businessClassification: String(item?.business_classification ?? ''),
      natureOfService: String(item?.nature_of_service ?? ''),
      rcNumber: String(item?.rc_number ?? ''),
      dateOfIncorporation: String(item?.date_of_incorporation ?? ''),
      tin: tinRaw || asin,
      tccNumber: String(item?.tcc_number ?? ''),
      vendorType: String(item?.vendor_type ?? ''),
      applicationStatus: String(item?.application_status ?? ''),
      createdAt: String(item?.created_at ?? ''),
    };
  }

  private mapVendorDetailsRow(item: any): VendorRow {
    const base = this.mapVendorRow(item);

    const directors = Array.isArray(item?.directors)
      ? item.directors.map((d: any) => ({
          asin: String(d?.asin ?? ''),
          name: String(d?.name ?? ''),
          phone: String(d?.phone_number ?? d?.phone ?? ''),
          email: String(d?.email ?? ''),
          address: String(d?.address ?? ''),
          bvn: String(d?.bvn ?? ''),
        }))
      : undefined;

    const bankDetails = item?.account
      ? {
          accountNumber: String(item.account?.account_number ?? ''),
          accountName: String(item.account?.account_name ?? ''),
          bankName: String(item.account?.bank_name ?? ''),
        }
      : item?.bank_details
        ? {
            accountNumber: String(item.bank_details?.account_number ?? ''),
            accountName: String(item.bank_details?.account_name ?? ''),
            bankName: String(item.bank_details?.bank_name ?? ''),
          }
        : undefined;

    const documents = Array.isArray(item?.documents)
      ? item.documents.map((doc: any) => ({
          name: String(doc?.document_name ?? doc?.name ?? ''),
          uploadedAt: String(doc?.updated_at ?? doc?.created_at ?? ''),
          uploaded_document_url: String(doc?.uploaded_document_url ?? doc?.uploadedDocumentUrl ?? ''),
        }))
      : undefined;

    const comments = Array.isArray(item?.comments)
      ? item.comments.map((d: any) => ({
          comment: String(d?.comment ?? ''),
          name: String(d?.name ?? ''),
          createdAt: String(d?.createdAt ?? ''),
        }))
      : undefined;

    return {
      ...base,
      directors,
      bankDetails,
      documents,
      comments
    };
  }
}
